import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchReviews } from '../lib/cms'
import { useDocumentMeta } from '../hooks/useDocumentMeta'

export default function Home() {
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)

  useDocumentMeta({
    title: 'Psykoterapeut Svendborg | Psykoterapi, Parterapi & Familieterapi på Fyn',
    description: 'Psykoterapeut i Svendborg, Birgitte Vilsgaard. Individuel psykoterapi i Svendborg, parterapi og familieterapi på Fyn i trygge rammer på Dronningemaen 68. Bestil tid her.',
    image: 'https://assets.auxodev.dk/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg'
  })

  useEffect(() => {
    async function loadData() {
      const reviewsData = await fetchReviews()
      setReviews(reviewsData)
      setLoading(false)
    }
    loadData()
  }, [])

  const serviceEntries = [
    {
      title: 'Individuel psykoterapi',
      description: 'Til dig, hvor stress, tankemylder, sorg eller relationer er blevet svære at stå alene med. Et fortroligt rum med fuldt fokus på dig.',
      link: '/ydelser/integrativ-psykoterapeut',
      icon: 'fa-light fa-spa',
      badge: 'Individuelt',
      ctaText: 'Læs om individuel terapi'
    },
    {
      title: 'Parterapi i Svendborg',
      description: 'Når kommunikationen er kørt fast, tilliden er svækket, eller I savner nærhed og gensidig forståelse. Nærværende parterapi for par fra hele Fyn.',
      link: '/ydelser/integrativ-psykoterapeut',
      icon: 'fa-light fa-comments',
      badge: 'Parforhold',
      ctaText: 'Læs om parterapi'
    },
    {
      title: 'Familiebehandling &\nSamværskonsulent',
      description: 'Helhedsorienteret familiebehandling for sårbare familier samt professionelt støttet og overvåget samvær med fokus på barnets trivsel, tryghed og udvikling.',
      link: '/ydelser',
      icon: 'fa-light fa-people-roof',
      badge: 'Familie & Samvær',
      ctaText: 'Læs om familie & samvær'
    },
    {
      title: 'Sorg, tab & livskriser',
      description: 'Når livet uventet forandrer sig, og du har brug for støtte til at bearbejde tab og genfinde fodfæste i dit eget tempo.',
      link: '/ydelser/temaer-sorg-og-tab',
      icon: 'fa-light fa-heart-crack',
      badge: 'Sorg & krise',
      ctaText: 'Læs om sorg og kriser'
    }
  ]

  const reflectionCards = [
    {
      title: 'Uro, stress og overbelastning',
      text: 'Når hverdagen føles overvældende, og krop eller tanker har svært ved at finde ro og balance.',
      icon: 'fa-light fa-feather-pointed'
    },
    {
      title: 'Udfordringer i parforhold eller familie',
      text: 'Når kommunikationen slår knuder, og I savner tryghed, nærhed eller gensidig respekt.',
      icon: 'fa-light fa-comments'
    },
    {
      title: 'Svært ved at sætte sunde grænser',
      text: 'Når du ofte tilpasser dig andre og overhører dine egne grænser, værdier og dybeste behov.',
      icon: 'fa-light fa-shield-heart'
    },
    {
      title: 'Sorg, tab eller en pludselig krise',
      text: 'Når tilværelsen slår en uventet kolbøtte, og du har brug for et sikkert anker til at finde fodfæste.',
      icon: 'fa-light fa-compass'
    },
    {
      title: 'Gentagne mønstre, du vil bryde',
      text: 'Når du oplever at havne i de samme uhensigtsmæssige roller eller reaktioner igen og igen.',
      icon: 'fa-light fa-rotate-left'
    },
    {
      title: 'Ønske om selvindsigt og personlig ro',
      text: 'Når du vil forstå dig selv dybere, styrke dit selvværd og skabe et liv i harmoni med den, du er.',
      icon: 'fa-light fa-seedling'
    }
  ]

  const processSteps = [
    {
      step: '1',
      title: 'Du tager kontakt',
      subtitle: 'Uforpligtende afklaring',
      text: 'Ring eller send en kort besked. Vi afstemmer kort hvad du søger hjælp til, og finder en tid, der passer dig.',
      icon: 'fa-light fa-phone-arrow-up-right',
      practical: 'Hurtigt svar · Ingen henvisning påkrævet'
    },
    {
      step: '2',
      title: 'Den første samtale',
      subtitle: 'Rolige rammer i Svendborg',
      text: 'Vi mødes i klinikken på Dronningemaen 68. Vi taler om din situation og dine ønsker, og du mærker efter om kemien er god.',
      icon: 'fa-light fa-mug-hot',
      practical: 'Trygt og uformelt · Kaffe/te og ro'
    },
    {
      step: '3',
      title: 'Et forløb i dit tempo',
      subtitle: 'Fleksibelt og uden binding',
      text: 'Der er intet fastlagt antal samtaler. Sammen tilrettelægger vi forløbet, så det giver optimal mening og værdi for dig.',
      icon: 'fa-light fa-compass',
      practical: 'Ingen binding · Helt i dit tempo'
    }
  ]

  const priceItems = [
    {
      title: 'Individuel psykoterapi',
      duration: '60 minutter',
      price: '850 kr.',
      description: 'Samtaleterapi med fuldt fokus på dig, dine personlige udfordringer og din trivsel.',
      badge: 'Mest valgte'
    },
    {
      title: 'Parterapi i Svendborg',
      duration: '90 minutter',
      price: '1.200 kr.',
      description: 'Fælles session for jer som par, hvor vi styrker dialogen og genskaber tilliden.',
      badge: 'Parforhold'
    },
    {
      title: 'Walk-and-Talk / Naturterapi',
      duration: '60 minutter',
      price: '850 kr.',
      description: 'Samtaler under åben himmel i den smukke sydfynske natur nær skov og vand.',
      badge: 'I naturen'
    }
  ]

  return (
    <div className="min-h-screen flex flex-col bg-brand-offwhite text-brand-gray">
      {/* 1. HERO SEKTION - ROLIGT, LUFTIGT & EKSKLUSIVT */}
      <section className="relative pt-32 pb-28 sm:pt-36 sm:pb-36 lg:pt-40 lg:pb-44 bg-brand-dark text-brand-offwhite overflow-hidden">
        {/* Baggrundsbillede med dæmpet overlay og rolig dybde */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg"
            alt="Psykoterapi i Svendborg - Terapien.dk"
            className="w-full h-full object-cover object-[center_35%] -scale-x-100"
          />
          {/* Overlay og gradient på baggrundsbilledet */}
          <div className="absolute inset-0 bg-brand-dark/25" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/70 via-brand-dark/35 to-brand-dark/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 via-transparent to-brand-dark/40 pointer-events-none" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
          <div className="max-w-3xl text-left bg-[#ECE7E0]/95 backdrop-blur-md p-8 sm:p-10 lg:p-12 rounded-3xl shadow-2xl border border-white/60 text-brand-gray mt-6 sm:mt-10 lg:mt-14">
            <div className="flex flex-col items-start gap-6 text-left">
              {/* Lille eksklusivt kvalitetsmærke */}
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-dark font-semibold bg-white/70 border border-brand-sand/50 px-4 py-1.5 rounded-full inline-flex items-center gap-2 shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-brand-sage" />
                  Terapien.dk · Nærvær · Ro · Plads til dig
                </span>
              </div>

              {/* Hovedoverskrift */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-brand-dark leading-[1.2] text-balance">
                Psykoterapi i Svendborg – et trygt rum til forandring, forståelse og udvikling
              </h1>

              {/* Kort, præcis intro */}
              <p className="text-sm sm:text-base text-brand-gray leading-relaxed font-sans">
                Jeg tilbyder psykoterapi til voksne, par og familier, der ønsker hjælp til at forstå og håndtere det, der er blevet svært. Det kan eksempelvis være relationer, stress, sorg, livskriser, selvværd eller tilbagevendende mønstre.
              </p>

              {/* 4 klare nøglepunkter: Hurtigt overblik i 2 kolonner inde i kassen */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 w-full text-left">
                <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/70 border border-brand-sand/40 shadow-2xs">
                  <div className="w-8 h-8 rounded-xl bg-brand-sage/20 text-brand-dark flex items-center justify-center shrink-0 text-sm">
                    <i className="fa-light fa-users" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-brand-dark/70 block">Hvem hjælper vi?</span>
                    <span className="text-xs sm:text-sm font-semibold text-brand-dark leading-tight block mt-0.5">Voksne, par og familier</span>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/70 border border-brand-sand/40 shadow-2xs">
                  <div className="w-8 h-8 rounded-xl bg-brand-sage/20 text-brand-dark flex items-center justify-center shrink-0 text-sm">
                    <i className="fa-light fa-hand-holding-heart" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-brand-dark/70 block">Hvad tilbyder vi?</span>
                    <span className="text-xs sm:text-sm font-semibold text-brand-dark leading-tight block mt-0.5">Psykoterapi &amp; samtaler</span>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/70 border border-brand-sand/40 shadow-2xs">
                  <div className="w-8 h-8 rounded-xl bg-brand-sage/20 text-brand-dark flex items-center justify-center shrink-0 text-sm">
                    <i className="fa-light fa-location-dot" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-brand-dark/70 block">Hvor foregår det?</span>
                    <span className="text-xs sm:text-sm font-semibold text-brand-dark leading-tight block mt-0.5">I Svendborg (Dronningemaen 68)</span>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-2xl bg-white/70 border border-brand-sand/40 shadow-2xs">
                  <div className="w-8 h-8 rounded-xl bg-brand-sage/20 text-brand-dark flex items-center justify-center shrink-0 text-sm">
                    <i className="fa-light fa-comments" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-brand-dark/70 block">Hvordan tager man kontakt?</span>
                    <span className="text-xs sm:text-sm font-semibold text-brand-dark leading-tight block mt-0.5">Ring eller send en besked</span>
                  </div>
                </div>
              </div>

              {/* Handlingsknapper */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Link
                  to="/kontakt"
                  className="bg-brand-dark hover:bg-brand-dark/90 text-brand-sand text-xs sm:text-sm font-semibold tracking-wide px-6 sm:px-7 py-3.5 rounded-full transition-all duration-300 shadow-sm hover:scale-[1.02] active:scale-[0.98]"
                >
                  Book en samtale
                </Link>
                <Link
                  to="/psykoterapi"
                  className="bg-white hover:bg-white/90 border border-brand-sand text-brand-dark text-xs sm:text-sm font-semibold tracking-wide px-5 sm:px-6 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2 shadow-2xs"
                >
                  <span>Læs om terapi</span>
                  <i className="fa-light fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/priser"
                  className="text-xs font-semibold uppercase tracking-wider text-brand-dark/75 hover:text-brand-dark underline underline-offset-4 px-2 py-2 transition-colors ml-1"
                >
                  Priser &amp; praktisk
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bølge overgang til næste sektion ("bølgen i") */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg
            className="relative block w-full h-10 sm:h-14 lg:h-20 text-[#F7F4EE]"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,32L60,42.7C120,53,240,75,360,80C480,85,600,75,720,58.7C840,43,960,21,1080,21.3C1200,21,1320,43,1380,53.3L1440,64L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </section>

      {/* 2. BEHANDLERPROFILEN RELATIVT HØJT PÅ FORSIDEN (MØD BIRGITTE) */}
      <section className="py-24 sm:py-28 lg:py-32 bg-[#F7F4EE] border-b border-brand-sand/50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Billede: Behandlerprofil med roligt, varmt nærvær */}
            <div className="lg:col-span-5 relative order-last lg:order-first">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* Diskret baggrundsflade der skaber dybde */}
                <div className="absolute inset-0 bg-brand-sand/80 rounded-3xl transform -rotate-1 translate-x-2 translate-y-2 -z-10" />
                
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg"
                  alt="Birgitte Vilsgaard - Psykoterapeut og pædagog i Svendborg"
                  className="w-full h-[430px] sm:h-[480px] object-cover object-[center_20%] rounded-3xl shadow-[0_16px_40px_rgba(0,0,0,0.06)] border-4 border-white"
                />

                {/* Faglig differentiering badge */}
                <div className="absolute -bottom-5 right-3 sm:-right-4 bg-white/95 backdrop-blur-md border border-brand-sand/80 rounded-2xl p-4 shadow-[0_12px_32px_rgba(0,0,0,0.07)] max-w-[270px]">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-brand-sage/15 text-brand-dark flex items-center justify-center shrink-0 text-xl">
                      <i className="fa-light fa-award" />
                    </div>
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-brand-sage block">Faglig profil</span>
                      <p className="text-xs sm:text-sm font-bold text-brand-dark leading-tight mt-0.5">
                        Pædagog + Psykoterapeut
                      </p>
                      <p className="text-[11px] text-brand-gray/70 mt-0.5">
                        Praksis i Svendborg · Terapi på Fyn
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tekst: Kort, varm og personlig introduktion uden tunge tekstblokke */}
            <div className="lg:col-span-7 flex flex-col gap-6 text-left">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-bold bg-white/90 border border-brand-sand/70 px-3.5 py-1.5 rounded-full inline-flex items-center gap-2 shadow-xs">
                  <span className="w-2 h-2 rounded-full bg-brand-sage" />
                  Mennesket bag terapien
                </span>
                <span className="text-xs uppercase tracking-wider font-semibold text-brand-dark/80 bg-brand-sand/40 border border-brand-sand/60 px-3 py-1.5 rounded-full">
                  Birgitte Vilsgaard
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-dark leading-tight">
                Mød Birgitte
              </h2>

              {/* Fremhævet kernebudskab med tydelig differentiering */}
              <div className="bg-white/80 border-l-4 border-brand-sage p-5 sm:p-6 rounded-r-2xl border border-brand-sand/60 shadow-xs">
                <p className="text-base sm:text-lg font-medium text-brand-dark leading-relaxed">
                  Jeg er <strong className="font-bold underline decoration-brand-sage decoration-2 underline-offset-2">uddannet psykoterapeut og pædagog</strong> med praksis i Svendborg. Kombinationen af pædagogisk nærvær og terapeutisk dybde giver et trygt, ufordømmende og relationelt afsæt for vores samtaler.
                </p>
              </div>

              {/* Kortere, mere luftig brødtekst - fordybelse på undersiden */}
              <p className="text-base text-brand-gray/85 leading-relaxed">
                Som psykoterapeut i Svendborg lægger jeg vægt på at skabe et rum, hvor der er plads til både det svære og det, der kan åbne nye muligheder for forandring. Uanset om du søger individuel psykoterapi i Svendborg, parterapi eller familieterapi på Fyn, tager vi altid udgangspunkt i netop dit liv og dine behov.
              </p>

              {/* Certificering & troværdighed */}
              <div className="pt-2 flex items-center gap-3 text-xs text-brand-gray/80">
                <i className="fa-light fa-circle-check text-brand-sage text-base" />
                <span>Medlem af Dansk Psykoterapeutforening / FaDP · Fuld tavshedspligt</span>
              </div>

              {/* Handlinger */}
              <div className="pt-3 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to="/om-mig"
                  className="inline-flex items-center gap-3 bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-sm font-semibold tracking-wide px-8 py-4 rounded-full transition-all duration-300 shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] group"
                >
                  <span>Læs mere om mig og min tilgang</span>
                  <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-white border border-brand-dark/25 hover:border-brand-dark text-brand-dark text-sm font-semibold tracking-wide px-7 py-4 rounded-full transition-all duration-300 hover:bg-brand-sand/20 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <span>Book en samtale</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3. YDELSER & KLARE INDGANGE (TYDELIGE CTA'ER UDEN TUNGE BLOKKE) */}
      <section className="py-24 sm:py-28 lg:py-32 bg-brand-offwhite relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="flex flex-col gap-3 max-w-2xl text-left">
              <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-semibold">Tydelige indgange</span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-dark">Find det rette forløb for dig</h2>
              <p className="text-base text-brand-gray/80 leading-relaxed">
                Her kan du hurtigt vælge den samtaleform, der matcher din situation. Hver ydelse tilbydes i rolige, uforstyrrede rammer i Svendborg med nem adgang fra hele Fyn.
              </p>
            </div>
            <div className="shrink-0">
              <Link
                to="/ydelser"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-brand-dark/25 hover:border-brand-dark text-brand-dark text-sm font-semibold hover:bg-brand-sand/20 transition-all duration-300 shadow-xs"
              >
                <span>Se alle ydelser</span>
                <i className="fa-light fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {serviceEntries.map((service, index) => (
              <div
                key={index}
                className="bg-white border border-brand-sand/70 rounded-3xl p-7 sm:p-8 shadow-[0_4px_25px_rgba(0,0,0,0.02)] hover:shadow-[0_16px_40px_rgba(56,79,99,0.08)] hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div className="h-12 w-12 rounded-2xl bg-brand-sand/40 flex items-center justify-center text-brand-dark text-xl group-hover:bg-brand-dark group-hover:text-brand-offwhite transition-all duration-300">
                      <i className={service.icon} />
                    </div>
                    <span className="text-[11px] font-semibold tracking-wider uppercase text-brand-sage bg-brand-sand/25 px-3 py-1 rounded-full border border-brand-sand/60">
                      {service.badge}
                    </span>
                  </div>

                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-brand-dark group-hover:text-brand-dark transition-colors whitespace-pre-line">
                    {service.title}
                  </h3>
                  <p className="text-sm text-brand-gray/80 leading-relaxed mt-3">
                    {service.description}
                  </p>
                </div>

                <div className="mt-8 pt-5 border-t border-brand-sand/40">
                  <Link
                    to={service.link}
                    className="w-full inline-flex items-center justify-between px-5 py-3 rounded-full bg-[#F5F2ED] group-hover:bg-brand-dark text-brand-dark group-hover:text-brand-offwhite text-xs sm:text-sm font-semibold tracking-wide transition-all duration-300 border border-brand-sand/70 group-hover:border-brand-dark"
                  >
                    <span>{service.ctaText}</span>
                    <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Diskret reference for myndigheder / supervision */}
          <div className="mt-12 text-center">
            <p className="text-xs sm:text-sm text-brand-gray/70">
              Løser du myndighedsopgaver eller søger du faglig supervision?{' '}
              <Link to="/ydelser" className="text-brand-dark font-semibold underline underline-offset-4 hover:text-brand-sage transition-colors">
                Læs om familiebehandling, samværskonsulent og supervision her
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* 4. SÅDAN FOREGÅR DET (ENKELT, OVERBOMLIGT OG TRYGT I 3 TRIN) */}
      <section id="forloeb" className="py-24 sm:py-28 lg:py-32 bg-[#F6F3ED] border-t border-b border-brand-sand/50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center flex flex-col gap-3 mb-16 max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-semibold">
              Enkelt og overskueligt
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-dark">
              Sådan foregår det
            </h2>
            <p className="text-base text-brand-gray/80 leading-relaxed">
              Det skal være trygt og overskueligt at tage det første skridt. Her ser du det enkle forløb fra første kontakt:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="bg-white border border-brand-sand/60 rounded-3xl p-8 sm:p-9 shadow-[0_4px_20px_rgba(0,0,0,0.02)] hover:shadow-[0_12px_32px_rgba(56,79,99,0.06)] transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="h-12 w-12 rounded-2xl bg-brand-dark text-brand-sand flex items-center justify-center font-serif font-bold text-xl shadow-xs">
                      {step.step}
                    </div>
                    <div className="h-10 w-10 rounded-full bg-brand-sage/15 flex items-center justify-center text-brand-dark">
                      <i className={step.icon} />
                    </div>
                  </div>

                  <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-sage block mb-1">
                    {step.subtitle}
                  </span>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-brand-dark mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-brand-gray/80 leading-relaxed">
                    {step.text}
                  </p>
                </div>

                <div className="pt-6 mt-6 border-t border-brand-sand/40 flex items-center gap-2 text-xs text-brand-gray/70">
                  <i className="fa-light fa-check text-brand-sage" />
                  <span>{step.practical}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Reassuring CTA bar */}
          <div className="mt-14 bg-white border border-brand-sand/70 rounded-3xl p-7 sm:p-9 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-[0_6px_25px_rgba(0,0,0,0.03)] text-center sm:text-left">
            <div className="max-w-xl">
              <h4 className="font-serif text-xl font-bold text-brand-dark">Klar til at tage det første skridt?</h4>
              <p className="text-sm text-brand-gray/80 mt-1">
                Du behøver ikke have svarene på forhånd. Du er altid velkommen til en uforpligtende afklaring.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3 shrink-0">
              <Link
                to="/kontakt"
                className="bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-xs sm:text-sm font-semibold tracking-wide px-7 py-3.5 rounded-full transition-all duration-200 hover:shadow-md hover:scale-[1.02] active:scale-95"
              >
                Book en samtale
              </Link>
              <Link
                to="/priser"
                className="bg-brand-offwhite border border-brand-dark/25 hover:border-brand-dark text-brand-dark text-xs sm:text-sm font-semibold tracking-wide px-6 py-3.5 rounded-full transition-all duration-200 hover:bg-white"
              >
                Priser &amp; praktisk
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PRISER & PRAKTISK INFORMATION (LETTILGÆNGELIGT DIREKTE PÅ FORSIDEN) */}
      <section className="py-24 sm:py-28 lg:py-32 bg-brand-offwhite relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="flex flex-col gap-3 max-w-2xl text-left">
              <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-semibold">Gennemskuelighed</span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-dark">Priser &amp; praktisk information</h2>
              <p className="text-base text-brand-gray/80 leading-relaxed">
                Her finder du et hurtigt overblik over honorarer for de mest almindelige forløb samt praktiske detaljer om klinikken i Svendborg.
              </p>
            </div>
            <div className="shrink-0">
              <Link
                to="/priser"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-dark text-brand-offwhite text-sm font-semibold hover:bg-brand-dark/90 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <span>Se alle priser &amp; vilkår</span>
                <i className="fa-light fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>

          {/* 3 klare priskort */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {priceItems.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-brand-sand/70 rounded-3xl p-8 shadow-[0_4px_25px_rgba(0,0,0,0.02)] flex flex-col justify-between hover:border-brand-sage/70 transition-all duration-300"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-sage bg-brand-sand/30 px-3 py-1 rounded-full border border-brand-sand/60">
                      {item.badge}
                    </span>
                    <span className="text-xs text-brand-gray/60 font-medium">
                      {item.duration}
                    </span>
                  </div>

                  <h3 className="font-serif text-2xl font-bold text-brand-dark">
                    {item.title}
                  </h3>

                  <div className="my-5 pb-5 border-b border-brand-sand/40">
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-3xl sm:text-4xl font-bold text-brand-dark">{item.price}</span>
                      <span className="text-xs text-brand-gray/70">pr. session</span>
                    </div>
                  </div>

                  <p className="text-sm text-brand-gray/80 leading-relaxed">
                    {item.description}
                  </p>
                </div>

                <div className="mt-8 pt-4">
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#F5F2ED] hover:bg-brand-dark text-brand-dark hover:text-brand-offwhite text-xs sm:text-sm font-semibold tracking-wide transition-all duration-200 border border-brand-sand/70"
                  >
                    <span>Book denne session</span>
                    <i className="fa-solid fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* 3 praktiske infokort (Beliggenhed, transport og tider) */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#FAF7F2] border border-brand-sand/60 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-brand-sage/20 text-brand-dark flex items-center justify-center shrink-0">
                <i className="fa-light fa-location-dot text-lg" />
              </div>
              <div>
                <h4 className="font-serif font-bold text-brand-dark text-base">Klinikken i Svendborg</h4>
                <p className="text-xs text-brand-gray/80 mt-1 leading-relaxed">
                  Dronningemaen 68, 5700 Svendborg. Rolige omgivelser med gratis parkering ved døren og 5 min. gang fra Svendborg Station.
                </p>
              </div>
            </div>

            <div className="bg-[#FAF7F2] border border-brand-sand/60 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-brand-sage/20 text-brand-dark flex items-center justify-center shrink-0">
                <i className="fa-light fa-clock text-lg" />
              </div>
              <div>
                <h4 className="font-serif font-bold text-brand-dark text-base">Samtaletider</h4>
                <p className="text-xs text-brand-gray/80 mt-1 leading-relaxed">
                  Tirsdag – torsdag: 08.00 – 17.00. Fredag: 08.00 – 15.00. Mulighed for fleksible tider efter forudgående aftale.
                </p>
              </div>
            </div>

            <div className="bg-[#FAF7F2] border border-brand-sand/60 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-brand-sage/20 text-brand-dark flex items-center justify-center shrink-0">
                <i className="fa-light fa-file-shield text-lg" />
              </div>
              <div>
                <h4 className="font-serif font-bold text-brand-dark text-base">Ingen henvisning nødvendig</h4>
                <p className="text-xs text-brand-gray/80 mt-1 leading-relaxed">
                  Du behøver ingen lægehenvisning eller forudgående visitation for at starte. Hurtig opstart uden venteliste.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. SPEJLING & GENKENDELIGHED (FÆRRE TUNGE BLOKKE - KORTE ROVE PUNKTER) */}
      <section className="py-24 sm:py-28 lg:py-32 bg-[#F6F3EE] border-t border-b border-brand-sand/50 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14 text-left">
            <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-bold bg-white/90 border border-brand-sand/70 px-3.5 py-1.5 rounded-full inline-flex items-center gap-2 shadow-xs mb-3">
              <span className="w-2 h-2 rounded-full bg-brand-sage" />
              Spejling &amp; genkendelighed
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-dark">
              Måske kan terapi hjælpe dig, hvis…
            </h2>
            <p className="text-base text-brand-gray/85 leading-relaxed mt-3">
              Du behøver hverken have en diagnose eller stå midt i en dyb krise for at gå i terapi.
              <br className="hidden sm:inline" /> Mange henvender sig blot, når hverdagen, relationerne eller tankerne føles for tunge at bære alene.
            </p>
          </div>

          {/* 6 klare, letlæselige spejlingskort */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reflectionCards.map((point, index) => (
              <div
                key={index}
                className="bg-white border border-brand-sand/70 rounded-2xl p-6 sm:p-7 shadow-[0_4px_20px_rgba(0,0,0,0.02)] flex flex-col justify-between hover:border-brand-sage/70 transition-all duration-300"
              >
                <div>
                  <div className="h-10 w-10 rounded-xl bg-brand-sand/40 text-brand-dark flex items-center justify-center text-lg mb-4">
                    <i className={point.icon} />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-brand-dark leading-snug">
                    {point.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-brand-gray/80 leading-relaxed mt-2.5">
                    {point.text}
                  </p>
                </div>
                <div className="mt-5 pt-3 border-t border-brand-sand/40 flex items-center justify-between text-xs text-brand-gray/70">
                  <span className="inline-flex items-center gap-1.5 text-brand-sage font-medium">
                    <i className="fa-light fa-check text-xs" />
                    <span>Plads til dig</span>
                  </span>
                  <Link to="/kontakt" className="text-brand-dark font-semibold hover:underline">
                    Få støtte
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-sm font-semibold tracking-wide px-8 py-4 rounded-full transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <span>Uforpligtende samtale – tag kontakt i dag</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. KLIENTERNES ORD / UDTALELSER */}
      <section className="py-24 sm:py-28 lg:py-32 bg-brand-offwhite overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-6 mb-12 text-center flex flex-col gap-3">
          <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-semibold">Tillid &amp; tryghed</span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-dark">Klienternes erfaringer</h2>
          <p className="text-sm text-brand-gray/75 max-w-xl mx-auto">
            Hør hvad tidligere klienter fortæller om mødet, nærværet og forandringen i klinikken.
          </p>
        </div>

        <div className="relative w-full overflow-hidden py-2">
          <div className="flex w-max gap-6 animate-scroll">
            {[...reviews, ...reviews].map((review, idx) => (
              <div
                key={idx}
                className="w-80 sm:w-96 flex-shrink-0 bg-white border border-brand-sand/60 rounded-3xl p-7 shadow-[0_6px_25px_rgba(0,0,0,0.02)] hover:shadow-[0_16px_36px_rgba(56,79,99,0.06)] transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  <div className="flex gap-1 text-amber-500/90 text-xs mb-4">
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                  </div>
                  <p className="italic text-brand-gray text-sm leading-relaxed mb-6">
                    "{review.tekst}"
                  </p>
                </div>
                <div className="border-t border-brand-sand/40 pt-4 mt-auto">
                  <h4 className="font-semibold text-brand-dark text-sm">{review.navn}</h4>
                  {review.titel && (
                    <span className="text-xs text-brand-sage mt-0.5 block">{review.titel}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. AFSLUTTENDE BOOKING & KONTAKT SEKTION */}
      <section className="py-24 sm:py-28 lg:py-32 bg-[#F5F1EB] border-t border-brand-sand/50 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-[2.5rem] overflow-hidden border border-brand-sand/70 shadow-[0_16px_50px_rgba(0,0,0,0.08)]">
            {/* Baggrundsbillede med dæmpet dybde og overlay */}
            <div className="absolute inset-0">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg"
                alt="Psykoterapeut Svendborg - Terapien.dk"
                className="w-full h-full object-cover object-[center_35%]"
              />
              <div className="absolute inset-0 bg-brand-dark/75" />
              <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/95 via-brand-dark/80 to-brand-dark/60" />
            </div>

            <div className="relative z-10 p-8 sm:p-12 lg:p-16">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                
                {/* Venstre kolonne: Varm personlig invitation på mørk baggrund */}
                <div className="lg:col-span-5 text-brand-offwhite flex flex-col justify-between">
                  <div className="flex flex-col gap-4">
                    <span className="inline-flex items-center gap-2 bg-white/10 text-brand-sand text-xs font-semibold px-3.5 py-1.5 rounded-full uppercase tracking-wider self-start backdrop-blur-sm border border-white/10">
                      <i className="fa-solid fa-calendar-check text-[10px]" /> Tag det første skridt
                    </span>
                    <h3 className="font-serif text-3xl sm:text-4xl text-brand-sand leading-tight">
                      Start din samtale mod forandring
                    </h3>
                    <p className="text-sm sm:text-base text-brand-offwhite/85 leading-relaxed mt-1">
                      Søger du en erfaren psykoterapeut i Svendborg til individuel terapi, parterapi eller familieterapi på Fyn? Du behøver ikke at stå alene med det, der er svært. Kontakt mig for en uforpligtende samtale.
                    </p>
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/20 flex flex-col gap-4">
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-brand-sand">
                        <i className="fa-light fa-phone text-lg" />
                      </div>
                      <div>
                        <span className="text-xs text-brand-sand/80 uppercase tracking-wider block font-medium">Ring direkte</span>
                        <a href="tel:28839488" className="text-xl sm:text-2xl font-bold text-white hover:text-brand-sand transition-colors">
                          28 83 94 88
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-brand-sand">
                        <i className="fa-light fa-location-dot text-lg" />
                      </div>
                      <div>
                        <span className="text-xs text-brand-sand/80 uppercase tracking-wider block font-medium">Klinikken</span>
                        <span className="text-sm font-medium text-white/95">Dronningemaen 68, 5700 Svendborg</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Højre kolonne: Lys, rolig kontaktformular i beige/hvid boks */}
                <div className="lg:col-span-7 bg-[#FAF8F5]/95 backdrop-blur-md rounded-3xl p-8 sm:p-10 border border-white/80 shadow-xl text-brand-gray">
                  <div className="mb-6 text-left">
                    <span className="text-xs uppercase tracking-wider text-brand-sage font-bold block mb-1">Skriv til Birgitte</span>
                    <h4 className="font-serif text-2xl sm:text-3xl font-bold text-brand-dark">Send en besked</h4>
                    <p className="text-xs sm:text-sm text-brand-gray/70 mt-1">
                      Jeg besvarer alle henvendelser med fuld diskretion hurtigst muligt.
                    </p>
                  </div>

                  <form
                    onSubmit={async (e) => {
                      e.preventDefault()
                      const form = e.target
                      const formData = new FormData(form)
                      
                      // Honeypot check
                      if (formData.get('website_url')) {
                        alert('Tak for din besked!')
                        form.reset()
                        return
                      }

                      try {
                        const res = await fetch('/_api/webform/submit', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            navn: formData.get('navn'),
                            email: formData.get('email'),
                            phone: formData.get('phone') || '',
                            message: formData.get('message'),
                            website_url: ''
                          })
                        })
                        if (res.ok) {
                          alert('Mange tak for din henvendelse. Jeg vender tilbage til dig hurtigst muligt.')
                          form.reset()
                        } else {
                          alert('Der skete en fejl ved afsendelsen. Ring gerne direkte på 28 83 94 88.')
                        }
                      } catch (err) {
                        alert('Der skete en fejl. Ring gerne direkte på 28 83 94 88.')
                      }
                    }}
                    className="flex flex-col gap-4 relative"
                  >
                    {/* Honeypot field (Antispam) */}
                    <div className="sr-only absolute" style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: '0' }}>
                      <input type="text" name="website_url" autoComplete="off" tabIndex="-1" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/70 mb-1.5">Dit navn *</label>
                        <input
                          type="text"
                          name="navn"
                          required
                          placeholder="F.eks. Mette Hansen"
                          className="w-full px-4 py-3.5 rounded-2xl bg-white border border-brand-sand focus:border-brand-sage focus:outline-none text-sm placeholder-brand-gray/40 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/70 mb-1.5">Din e-mail *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="din@email.dk"
                          className="w-full px-4 py-3.5 rounded-2xl bg-white border border-brand-sand focus:border-brand-sage focus:outline-none text-sm placeholder-brand-gray/40 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/70 mb-1.5">Telefonnummer (valgfrit)</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="F.eks. 12 34 56 78"
                        className="w-full px-4 py-3.5 rounded-2xl bg-white border border-brand-sand focus:border-brand-sage focus:outline-none text-sm placeholder-brand-gray/40 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/70 mb-1.5">Kort om hvad du søger hjælp til *</label>
                      <textarea
                        name="message"
                        required
                        placeholder="Skriv gerne et par linjer om din situation eller hvad du ønsker hjælp til..."
                        rows="3"
                        className="w-full px-4 py-3.5 rounded-2xl bg-white border border-brand-sand focus:border-brand-sage focus:outline-none text-sm placeholder-brand-gray/40 resize-none transition-colors"
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full py-4 rounded-full bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-sm font-semibold tracking-wide uppercase transition-all duration-300 hover:shadow-md hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
                    >
                      <span>Send besked</span>
                      <i className="fa-solid fa-arrow-right text-xs" />
                    </button>
                    <p className="text-[11px] text-center text-brand-gray/60 mt-1">
                      Dine oplysninger behandles fortroligt i overensstemmelse med gældende lovgivning.
                    </p>
                  </form>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
